import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../components/partials/components/loader";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import {
  API_MANAGE_LOCATIONS_LIST, // GET /vX/locations
  API_USERS_LIST,            // GET /vX/users
  API_BULK_LOCATION_DELETE,  // POST /vX/locations/bulk-delete  (add in url.js)
} from "../../../service/url";
import { toast } from 'react-toastify';

export default function BulkLocationDelete() {
  const [loading, setLoading] = useState(false);

  // master lists
  const [locations, setLocations] = useState([]);   // [{location_id, location_name}]
  const [allUsers, setAllUsers] = useState([]);     // full users list from API

  // working (filtered) list shown in table
  const [users, setUsers] = useState([]);

  // UI state
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [locRes, usersRes] = await Promise.all([
          getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
          getApiCall(API_USERS_LIST, {}),
        ]);
        setLocations(Array.isArray(locRes) ? locRes : []);
        setAllUsers(Array.isArray(usersRes) ? usersRes : []);
        setUsers([]); // start empty like the reference UI
      } catch (err) {
        toast.error("Failed to load locations/users");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter button: show only users who belong to the selected location
  const applyLocationFilter = () => {
    if (!selectedLocationId) {
      toast.error("Please select a location first.");
      return;
    }
    const lid = String(selectedLocationId);
    const filtered = (allUsers || []).filter((u) =>
      Array.isArray(u.userLocations) &&
      u.userLocations.some((ul) => String(ul?.locations?.location_id) === lid)
    );
    setUsers(filtered);
    console.log("filtered", filtered);
    setSelectedUserIds([]);
  };


  
  const clearFilter = () => {
    setUsers([]);
    setSelectedUserIds([]);
    setSearchValue("");
  };

  // Search within the filtered list
  const searched = useMemo(
    () =>
      (users || []).filter((u) =>
        (u.username || "").toLowerCase().includes(searchValue.toLowerCase())
      ),
    [users, searchValue]
  );

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // Select-all respects current search
  const toggleAll = () => {
    const visibleIds = searched.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedUserIds.includes(id));

    if (allSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const locationNamesBody = (row) => {
    if (!Array.isArray(row.userLocations)) return "-";
    const lid = selectedLocationId ? String(selectedLocationId) : null;
    const names = row.userLocations
      .filter((ul) => (lid ? String(ul?.locations?.location_id) === lid : true))
      .map((ul) => ul?.locations?.location_name)
      .filter(Boolean);
    return names.length ? names.join(", ") : "-";
  };

  const handleBulkDelete = async () => {
    if (!selectedLocationId) {
      toast.error("Please select a location first.");
      return;
    }
    if (selectedUserIds.length === 0) {
      toast.error("Select at least one user.");
      return;
    }

    try {
      setButtonDisable(true);
      const payload = { user_ids: selectedUserIds, location_id: selectedLocationId };
      const res = await postApiCall(API_BULK_LOCATION_DELETE, payload);

      if (res) {
        toast.success(res.message || "Users removed from location successfully");
        applyLocationFilter(); // refresh list
        setSelectedUserIds([]);
      } else {
        toast.error(res?.message || "Failed to remove users");
      }
    } catch (err) {
      toast.error(err?.message || "Error removing users from location");
    } finally {
      setButtonDisable(false);
      setConfirmVisible(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div >
      <Row style={{marginTop:"20px"}}>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk User Location Deletion</h4>
            </Card.Header>

            <Card.Body className="px-0">
              {/* Controls Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  flexWrap: "wrap",
                }}
              >
                {/* Show X items */}
                <span>Show</span>
                <Dropdown
                  value={rows}
                  options={[
                    { label: "5", value: 5 },
                    { label: "10", value: 10 },
                    { label: "20", value: 20 },
                    { label: "50", value: 50 },
                  ]}
                  onChange={(e) => setRows(e.value)}
                  style={{ width: 90, height: 40, display: "flex", alignItems: "center" }}
                />
                <span>items</span>

                {/* Search */}
                <Form.Control
                  style={{ width: 250, height: 40, marginLeft: 10 }}
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />

                <Button label="Clear" className="p-button-secondary" onClick={clearFilter} />

                {/* Location selector + Filter */}
                <Dropdown
                  value={selectedLocationId}
                  options={(locations || []).map((l) => ({ label: l.location_name, value: l.location_id }))}
                  onChange={(e) => setSelectedLocationId(e.value)}
                  placeholder="Select Location"
                  style={{ width: 260, marginLeft: 10 }}
                />
                <Button label="Filter" className="p-button-secondary" onClick={applyLocationFilter} />

                {/* Delete Selected */}
                <Button
                  label="Delete Selected"
                  className="p-button-danger"
                  disabled={selectedUserIds.length === 0 || !selectedLocationId || buttonDisable}
                  onClick={() => setConfirmVisible(true)}
                />
              </div>

              {/* Table */}
              <div className="scroll-container">
                <DataTable
                  value={searched}
                  paginator
                  rows={rows}
                  tableStyle={{ minWidth: "50rem" }}
                  emptyMessage="No data available"
                >
                  <Column
                    header={
                      <Checkbox
                        checked={
                          searched.length > 0 &&
                          searched.every((u) => selectedUserIds.includes(u.id))
                        }
                        onChange={toggleAll}
                      />
                    }
                    headerStyle={{ width: "3rem" }}
                    body={(row) => (
                      <Checkbox
                        checked={selectedUserIds.includes(row.id)}
                        onChange={() => toggleUser(row.id)}
                      />
                    )}
                  />
                  <Column field="id" header="User Id" sortable />
                  <Column field="username" header="User Name" sortable />
                  <Column header="Location Name" body={locationNamesBody} sortable />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Dialog
        header="Confirm Bulk Delete"
        visible={confirmVisible}
        style={{ width: "25vw" }}
        onHide={() => setConfirmVisible(false)}
        footer={
          <div>
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmVisible(false)} />
            <Button label="Confirm" className="p-button-danger" onClick={handleBulkDelete} disabled={buttonDisable} />
          </div>
        }
      >
        <p>Are you sure you want to remove {selectedUserIds.length} user(s) from this location?</p>
      </Dialog>
    </div>
  );
}
